import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { useUserContext } from '../../context/UserContext';
import GetStartedModal from '../../components/GetStartedModal';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Alert, Snackbar } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';

import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import Loading from '../../components/layout/Loading';
import ErrorMessage from '../../components/layout/ErrorMessage';

const SignIn = () => {
    const navigate = useNavigate();
    const { login, token } = useUserContext();

    const [formValues, setFormValues] = useState({ username: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [signInSuccess, setSignInSuccess] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // Redirect if user is already signed in
    useEffect(() => {
        if (token) {
            navigate('/');
        }
    }, [token, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: value }));
        if (value) setErrors(prev => ({ ...prev, [name]: false }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formValues.username) newErrors.username = true;
        if (!formValues.password) newErrors.password = true;
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!validate()) {
            setErrorMessage('Please fill in all the required fields.');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post('/user/login', formValues);
            console.log(response.data)
            const { token } = response.data;
            const decoded = jwtDecode(token);
            const username = decoded.preferred_username || decoded.username || decoded.email || '';
            login(token, username);
            setSignInSuccess(true);
            navigate('/'); // Immediate redirect
        } catch {
            setErrorMessage('Invalid username or password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => setSignInSuccess(false);

    return (
        <>
            <CssBaseline />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    minHeight: '100vh',
                    height: 'auto',
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #fefefe 100%)',
                    overflow: 'auto',
                }}
            >
                <Box
                    sx={{
                        width: '30%',
                        backgroundImage: `url(https://eu-images.contentstack.com/v3/assets/blt31d6b0704ba96e9d/blt88a7087d82cacbf3/655e23392ab1fb0407ec12a9/GettyImages-582763822.jpg?width=850&auto=webp&quality=95&format=jpg&disable=upscale)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: { xs: 'none', md: 'block' },
                        position: 'relative',
                        opacity: '90%'
                    }}
                />
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 4,
                        overflow: 'auto',
                    }}
                >
                    <Fade in timeout={800}>
                        <Card sx={{ width: { xs: '90%', sm: '70%', md: '60%' }, borderRadius: 4, boxShadow: 6, p: 3, background: 'rgba(255,255,255,0.95)', maxHeight: { xs: '95vh', sm: '90vh' }, overflowY: 'auto' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', boxShadow: 3, width: 56, height: 56 }} className={'fancyButton'}>
                                    <LoginOutlinedIcon fontSize="large" />
                                </Avatar>
                                <Typography component="h1" variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Sign In</Typography>
                                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    {['username', 'password'].map((field) => {
                                        const labelMap = {
                                            username: 'Username or Email',
                                            password: 'Password'
                                        };
                                        return (
                                            <TextField
                                                key={field}
                                                onChange={handleChange}
                                                color="secondary"
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                fullWidth
                                                id={field}
                                                type={field === 'password' && !showPassword ? 'password' : 'text'}
                                                label={labelMap[field]}
                                                name={field}
                                                autoComplete={field}
                                                autoFocus={field === 'username'}
                                                error={!!errors[field]}
                                                sx={{ width: '80%' }}
                                                InputProps={field === 'password' ? {
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={handleClickShowPassword}
                                                            >
                                                                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                } : null}
                                            />
                                        );
                                    })}
                                    <Box sx={{ mt: 3, mb: 2, width: '80%' }}>
                                        {loading && <Loading />}
                                        <Button
                                            sx={{
                                                color: 'white',
                                                mt: 2,
                                                width: '100%',
                                                background: 'linear-gradient(to right, #1976D2, #40C4FF)',
                                                boxShadow: 3,
                                                fontWeight: 600,
                                                letterSpacing: 1,
                                                '&:hover': {
                                                    background: 'linear-gradient(to right, #1976D2, #40C4FF)',
                                                },
                                            }}
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            className={'fancyButton'}
                                        >
                                            <Typography>SIGN IN</Typography>
                                        </Button>
                                    </Box>
                                </Box>
                                {errorMessage && <ErrorMessage message={errorMessage} />}
                                <Divider sx={{ width: '80%', my: 2 }} />
                                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                                    Don&apos;t have an account?{' '}
                                    <Button
                                        variant="text"
                                        onClick={() => setModalOpen(true)}
                                        sx={{
                                            color: '#6366f1',
                                            fontWeight: 500,
                                            textDecoration: 'none',
                                            textTransform: 'none',
                                            p: 0,
                                            minWidth: 'auto'
                                        }}
                                    >
                                        Get Started!
                                    </Button>
                                </Typography>
                            </CardContent>
                        </Card>
                    </Fade>
                    <Snackbar open={signInSuccess} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                        <Alert variant="filled" onClose={handleCloseSnackbar} severity="success">
                            Logged in successfully!
                        </Alert>
                    </Snackbar>
                </Box>
            </Box>

            <GetStartedModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
            />
        </>
    );
};

export default SignIn;
